import { numericality } from 'redux-form-validators'
import {
  skipRequiredThenValidate,
  lengthMax,
  validateFlow,
  weightNumberLimit,
  validateUnderOrEqual,
  couponTitleLengthMax
} from './validations'
import { positiveInteger, noEmoji, without } from '../../../../../../lib/Validator'
import _ from 'lodash'
import {
  getActiveTransientData,
  getSpecAttributeFieldByLevel,
  isSkipRequired,
  signedApplianceContract,
  getScopedFieldNameByShipType,
  determineSpecLevel
} from '../util'
import {
  getHasAnyErrorFile,
  filterFileListByError
} from '../../../../../../lib/uploaderUtil'
import {
  chunkUploaderDataByDimension,
  UPLOADER_DUMMY_ERROR_FILES,
  convertProposalImagesToUploaderData
} from '../../../ImageUploader/ImageUploaderHelper'
import { getShipTypeIdFromAllValues } from '../../../../../../lib/ReduxFormSelectors/ProposalListingForm'
import { PRODUCT_SHIP_TYPE, FIELD_MAX_NUM_VAL, PROPOSAL_TYPE, LARGE_VOLUME_RULE } from '@vzmi/shp_shared_compo/constants/proposal'
import { required, getDollarFormatted } from '../../../../../../lib/FormHelper'
import { isNewListingProposal } from '../../../../../../../lib/proposalHelper'
import { COUPON_FIELD } from '../../../../../Dashboard/OperatingDashboard/CustomerInterest/ECouponModal/ECouponModal'

// 賣場級別驗證，
export const validateContentRating = skipRequiredThenValidate(
  (value, allValues) => {
    const proposalType = _.get(allValues, 'setting.proposalType', PROPOSAL_TYPE.NEW_LISTING)
    const isNewListing = isNewListingProposal(proposalType)
    const msg = `請選擇${isNewListing ? '賣場' : '商品'}級別`
    return required({ msg })(value)
  }
)

// 最小包裝驗證
export const validateMinPackingCount = skipRequiredThenValidate(
  required({ msg: '最小包裝數不得 < 1' }),
  positiveInteger,
  validateUnderOrEqual(FIELD_MAX_NUM_VAL.MIN_PACKING_COUNT)
)

// 商品重量驗證
export const validateProductWeight = skipRequiredThenValidate(
  required({ msg: '請輸入商品重量' }),
  positiveInteger,
  (value, allValues) => {
    const shipTypeId = getShipTypeIdFromAllValues(allValues)
    const productNameFieldByShipType = getScopedFieldNameByShipType(shipTypeId, 'product')
    const scopedProduct = _.get(allValues, productNameFieldByShipType, {})
    const isNeedRecycle = _.get(allValues, 'product.isNeedRecycle', false)
    const { isInstallRequired } = scopedProduct
    const isSpecialLimitRequired = isNeedRecycle || isInstallRequired

    const applianceContract = signedApplianceContract(allValues)

    if (
      shipTypeId === PRODUCT_SHIP_TYPE.EXPRESS_24HR &&
      _.toNumber(value) > LARGE_VOLUME_RULE.WEIGHT &&
      (
        (applianceContract && !isSpecialLimitRequired) ||
        !applianceContract
      )
    ) {
      // 若配送方式選擇"快速到貨"
      // 未簽署"家電增補附約"時，重量需 <= 30,000g
      // 有簽署"家電增補附約", 但未勾需安裝時，重量需 <= 30,000g

      const formattedLargeVolumeWeight = getDollarFormatted(LARGE_VOLUME_RULE.WEIGHT)
      return `重量需 <= ${formattedLargeVolumeWeight}g`
    }
  },
  weightNumberLimit
)

export const validateCustomizedOrderShipDateAfterPlaced = skipRequiredThenValidate(
  required({ msg: '請輸入天數' }),
  numericality({ '>=': 2, '<=': 90, msg: '只能輸入2~90天' }),
  positiveInteger
)

// [保存天數]選"有"，但未填寫天數，儲存草稿需擋下
// using validateFlow instead of skipRequiredThenValidate to avoid skipRequired from invoking
export const validatePreserveDays = validateFlow(
  required({ msg: '請輸入保存天數' }),
  positiveInteger,
  numericality({ '>=': 1, msg: '請輸入正整數' }),
  validateUnderOrEqual(FIELD_MAX_NUM_VAL.PRESERVE_DAYS)
)

export const validateDuplicateSpec = (fieldIndex) => (value, values, props) => {
  const activeTransientData = getActiveTransientData(values)
  if (activeTransientData) {
    const { withSpecLevel: hasSpecLevel } = determineSpecLevel(activeTransientData.hasSpecLevel)
    const spec = _.get(activeTransientData, 'spec', [])
    const attributes = _.get(activeTransientData, getSpecAttributeFieldByLevel(hasSpecLevel), [])
    const existSpecNames = _.compact(_.map(spec, localSpec => {
      const { selectedOption = {} } = localSpec
      const { value } = selectedOption
      return value
    }))
    const isDuplicatedAttr = _.some(attributes, (attr, index) => {
      return attr.name === value && index !== fieldIndex
    })
    return _.includes(existSpecNames, value) || isDuplicatedAttr ? '不可輸入重複屬性' : null
  }
  return null
}

export const validateProposalImages = (value = [], values, props) => {
  const skipRequired = isSkipRequired(values)
  const {
    mainAndReviewImages,
    otherSizeImages
  } = chunkUploaderDataByDimension(convertProposalImagesToUploaderData(value))

  const mainAndReviewImagesError = getHasAnyErrorFile(mainAndReviewImages)
  const otherSizeImagesError = getHasAnyErrorFile(otherSizeImages)
  // 如果目前檔案內沒有預設主圖預設圖錯誤，而且為儲存草稿功能則跳過檢查
  if (skipRequired) {
    if (_.isEmpty(filterFileListByError(_.differenceBy(mainAndReviewImages, UPLOADER_DUMMY_ERROR_FILES, 'uid'))) && _.isEmpty(_.differenceBy(otherSizeImages, UPLOADER_DUMMY_ERROR_FILES, 'uid'))) {
      return null
    }
  }

  if (mainAndReviewImagesError || otherSizeImagesError) {
    return '商品圖片有誤'
  }

  const firstImageUrl = _.get(value, '0.url')
  if (_.isEmpty(firstImageUrl)) {
    return '請上傳商品圖片'
  }
  return null
}

export const validateModelImages = skipRequiredThenValidate(
  required({ msg: '請上傳商品圖片' }),
  validateProposalImages
)

/**
 * 自訂規格驗證
 * @param {('key'|'values')} type 自訂規格標題或內容
 * @param {string} fieldName 欄位在 allValues 中的位置
 * @param {number} fieldIdx 規格 offset
 */
export const getCustomAttrItemValidation = (type = '', fieldName = '', fieldIdx) => (value, allValues, props, name) => {
  let correspondingType
  let requiredMsg
  const validations = []
  switch (type) {
    case 'name': {
      correspondingType = 'values'
      requiredMsg = '輸入完整規格'
      // key 才需要檢查是否有重複
      validations.push(validateDuplicateSpec(fieldIdx))
      break
    }
    case 'values': {
      correspondingType = 'name'
      requiredMsg = '請輸入規格內容'
      break
    }
    default: {
      return undefined
    }
  }
  const isCorrespondingFieldEmpty = _.isEmpty(_.get(allValues, `${fieldName}.${correspondingType}`))
  !isCorrespondingFieldEmpty &&
    validations.unshift(without({ regex: /<|>/i, msg: '不允許字元< >' })) &&
    validations.unshift(required({ msg: requiredMsg }))
  return validateFlow(...validations)(value, allValues, props, name)
}

export const validateProductShortDescription = skipRequiredThenValidate(
  required({ msg: '請輸入商品特色' })
)

export const validateRequiredProductShortDescription = skipRequiredThenValidate(
  required({ msg: '請輸入商品特色' }),
  lengthMax('SHORT_DESC')
)

export const validateRequiredUpdateListingBrandId = skipRequiredThenValidate(
  required({ msg: '請選擇商品品牌' })
)

export const validateNotRequiredProductShortDescription = skipRequiredThenValidate(
  lengthMax('SHORT_DESC')
)

export const customWarrantyScopeValidation = validateFlow(
  required({ msg: '請輸入保固範圍' }),
  noEmoji({ msg: '請勿使用表情符號' }),
  lengthMax('WARRANTY_SCOPE')
)

export const COUPON_FIELD_TITLE_MAX_LENGTH = 41
export const COUPON_FIELD_DISCOUNT_COUNT_MAX_LIMIT = 999
export const COUPON_FIELD_DISCOUNT_AMOUNT_MAX_LIMIT = 9999
export const validateCouponTitle = validateFlow(
  required({ msg: '請輸入折價券名稱' }),
  couponTitleLengthMax(COUPON_FIELD_TITLE_MAX_LENGTH)
)

export const validateCouponDiscountCount = (yesterdayQuantity = 1) => (value = 1, allValues) => {
  let message = '只能輸入 1 位'
  if (yesterdayQuantity > 1) {
    message = `只能輸入 1~${yesterdayQuantity} 位`
    if (yesterdayQuantity > COUPON_FIELD_DISCOUNT_COUNT_MAX_LIMIT) {
      message = `只能輸入 1~${COUPON_FIELD_DISCOUNT_COUNT_MAX_LIMIT} 位`
    }
  }
  return validateFlow(
    required({ msg: '請輸入折抵人數' }),
    numericality({ '>=': 1, '<=': yesterdayQuantity, msg: message })
  )(value, allValues)
}

export const validateCouponMinQtyDiscountAmount = (cost, batchPrefix) => (value, allValues = {}) => {
  // 如果是批次發送折價券, 則需要考慮 batchPrefix 來抓正確的欄位
  const minQtyDiscountThreshold = batchPrefix
    ? _.get(allValues, `${batchPrefix}${COUPON_FIELD.MIN_QTY_DISCOUNT_THRESHOLD}`)
    : _.get(allValues, COUPON_FIELD.MIN_QTY_DISCOUNT_THRESHOLD)

  if (minQtyDiscountThreshold) {
    const minqtyLimit = minQtyDiscountThreshold * cost
    const newMinqtyLimitText = minqtyLimit > COUPON_FIELD_DISCOUNT_AMOUNT_MAX_LIMIT
      ? COUPON_FIELD_DISCOUNT_AMOUNT_MAX_LIMIT
      : minqtyLimit
    return validateFlow(
      required({ msg: '請輸入折抵金額' }),
      numericality({ '>': 0, '<': newMinqtyLimitText, msg: `不能超過 ${newMinqtyLimitText} 元` })
    )(value, allValues)
  }
}

export const validateCouponMinTotalDiscountAmount = (cost, price, batchPrefix) => (value, allValues = {}) => {
  // 如果是批次發送折價券, 則需要考慮 batchPrefix 來抓正確的欄位
  const minTotalDiscountThreshold = batchPrefix
    ? _.get(allValues, `${batchPrefix}${COUPON_FIELD.MIN_TOTAL_DISCOUNT_THRESHOLD}`)
    : _.get(allValues, COUPON_FIELD.MIN_TOTAL_DISCOUNT_THRESHOLD)

  if (minTotalDiscountThreshold) {
    const minqtyLimit = cost * Math.ceil(minTotalDiscountThreshold / price)
    const newMinqtyLimitText = minqtyLimit > COUPON_FIELD_DISCOUNT_AMOUNT_MAX_LIMIT
      ? COUPON_FIELD_DISCOUNT_AMOUNT_MAX_LIMIT
      : minqtyLimit

    return validateFlow(
      required({ msg: '請輸入折抵金額' }),
      numericality({ '>': 0, '<': newMinqtyLimitText, msg: `不能超過 ${newMinqtyLimitText} 元` })
    )(value, allValues)
  }
}

export const validateCouponDuration = (endTs) => {
  const now = new Date()
  const gdEndDate = new Date(endTs)
  const maxValidDays = Math.floor((gdEndDate - now) / (24 * 60 * 60 * 1000))
  return validateFlow(
    required({ msg: '請輸入有效天數' }),
    numericality({ '>=': 1, '<=': maxValidDays, msg: '有效天數不可超過賣場結束時間。' })
  )
}
